import type { ScenarioPackage, StudyTranslationSupport } from "@mobile/runtime/types";

import type { SupportLanguage } from "../appModel";
import { callPublicWordinariusFunction } from "../platform/firebaseFunctions";
import {
  cacheInstalledWebContentPackage,
  cacheWebContentCatalog,
  readAllInstalledWebContentPackages,
  readCachedWebContentCatalog,
  readInstalledWebContentPackage,
} from "./webContentCache";
import { validateSignedWebContentCatalog, validateWebContentArtifact } from "./webContentIntegrity";
import { mergeWebScenarioOverlay } from "./webScenarioOverlay";
import type {
  InstalledWebContentPackage,
  ResolvedWebScenario,
  SignedWebContentCatalog,
  WebContentCatalogEntry,
} from "./webContentTypes";

interface CatalogResponse {
  ok: true;
  catalogVersion: string;
  manifest: unknown;
  notModified: boolean;
}

export async function loadAllInstalledWebContentPackages(
  catalog: SignedWebContentCatalog,
  targetLanguage: string,
): Promise<InstalledWebContentPackage[]> {
  const cached = await readAllInstalledWebContentPackages();
  const currentEntries = new Map(catalog.entries
    .filter((entry) => entry.targetLanguage === targetLanguage)
    .map((entry) => [entry.packageId, entry]));
  const installed: InstalledWebContentPackage[] = [];
  for (const candidate of cached) {
    // Package validation can be CPU-heavy. Yield between cached packages so
    // background library hydration cannot monopolize the browser main thread.
    await new Promise<void>((resolve) => setTimeout(resolve, 0));
    const entry = currentEntries.get(candidate.entry.packageId);
    if (!entry || !isCachedPackageCurrent(candidate, entry)) continue;
    try {
      validateWebContentArtifact(candidate.artifact, entry);
      installed.push({ ...candidate, entry });
    } catch {
      // A stale or corrupted package remains ignored until it is reinstalled.
    }
  }
  return installed;
}

interface ArtifactResponse {
  ok: true;
  packageId: string;
  contentHash: string;
  artifact: unknown;
}

export async function loadWebContentCatalog(): Promise<SignedWebContentCatalog> {
  const cached = await readCachedWebContentCatalog();
  try {
    const response = await callPublicWordinariusFunction<CatalogResponse>(
      "getDomainContentDiscoveryCatalog",
      { currentCatalogVersion: cached?.catalogVersion || "" },
    );
    if (response.notModified && cached) {
      return validateSignedWebContentCatalog(cached);
    }
    const catalog = await validateSignedWebContentCatalog(response.manifest);
    await cacheWebContentCatalog(catalog);
    return catalog;
  } catch (error) {
    if (cached) {
      return validateSignedWebContentCatalog(cached);
    }
    throw error;
  }
}

function isCachedPackageCurrent(
  cached: InstalledWebContentPackage | null,
  entry: WebContentCatalogEntry,
): cached is InstalledWebContentPackage {
  return Boolean(
    cached
    && cached.entry.packageId === entry.packageId
    && cached.entry.contentHash === entry.contentHash
    && cached.artifact.contentHash === entry.contentHash,
  );
}

export async function loadInstalledWebContentPackage(
  entry: WebContentCatalogEntry,
): Promise<InstalledWebContentPackage | null> {
  const cached = await readInstalledWebContentPackage(entry.packageId);
  if (!isCachedPackageCurrent(cached, entry)) {
    return null;
  }
  try {
    validateWebContentArtifact(cached.artifact, entry);
    return cached;
  } catch {
    return null;
  }
}

export async function installWebContentPackage(
  entry: WebContentCatalogEntry,
  onProgress?: (progress: number | null) => void,
): Promise<InstalledWebContentPackage> {
  const cached = await loadInstalledWebContentPackage(entry);
  if (cached) {
    onProgress?.(100);
    return cached;
  }
  onProgress?.(null);
  const response = await callPublicWordinariusFunction<ArtifactResponse>(
    "getDomainContentPackageArtifact",
    { packageId: entry.packageId, contentHash: entry.contentHash },
  );
  onProgress?.(70);
  const artifact = validateWebContentArtifact(response.artifact, entry);
  const installed: InstalledWebContentPackage = {
    entry,
    artifact,
    installedAt: new Date().toISOString(),
  };
  await cacheInstalledWebContentPackage(installed);
  onProgress?.(100);
  return installed;
}

export function resolveWebScenario(
  installed: InstalledWebContentPackage,
  scenarioId: string,
  supportLanguage: SupportLanguage,
): ResolvedWebScenario {
  const metadata = installed.entry.scenarios.find((item) => item.scenarioId === scenarioId);
  if (!metadata) {
    throw new Error("content_scenario_metadata_missing");
  }
  const basePath = installed.entry.indexPath.replace(/index\.json$/, metadata.file);
  const baseFile = installed.artifact.files.find((file) => file.path === basePath);
  if (!baseFile) {
    throw new Error("content_scenario_missing");
  }
  const overlayPath = `content/locale_overlays/runtime/${supportLanguage}/${installed.entry.targetLanguage}/${installed.entry.level}/${installed.entry.domain}/${metadata.file}`;
  const overlayFile = installed.artifact.files.find((file) => file.path === overlayPath);
  const translationPath = `content/translation_support/runtime/${supportLanguage}/${installed.entry.targetLanguage}/${installed.entry.level}/${installed.entry.domain}/${metadata.file}`;
  const translationFile = installed.artifact.files.find((file) => file.path === translationPath);
  const mergedScenario = mergeWebScenarioOverlay(
    baseFile.payload as ScenarioPackage,
    overlayFile ? overlayFile.payload as Partial<ScenarioPackage> : null,
    supportLanguage,
  );
  const translationSupport = translationFile?.payload as StudyTranslationSupport | undefined;
  const validTranslationSupport = translationSupport?.contentType === "studyTranslationSupport"
    && translationSupport.scenarioId === scenarioId
    && translationSupport.targetLanguage === installed.entry.targetLanguage
    && translationSupport.uiLanguage === supportLanguage;
  return {
    scenario: validTranslationSupport
      ? { ...mergedScenario, translationSupport }
      : mergedScenario,
    metadata,
    entry: installed.entry,
  };
}
